/* =============================================================================
   Crumpaw · Nameplate hero — the nameplate, the chips, the panel, the dialog

   Loaded after cp-signature-bowl.js and only by cp-np-product-hero.liquid.
   The shared controller still owns colour, size, price, the name text and the
   Add to cart state. This file adds what it has no concept of:

     1. the nameplate — five slots around the name (left, right, a frame behind
        it, one stacked above, one stacked under), each with the layout's own
        scale and nudge, painted from cp-np-motif.liquid;
     2. the picker chips — each a live miniature of the finished band, showing
        the typed name in the chosen lettering style;
     3. the Step 2 preview panel and the full-preview dialog.

   The panel and dialog copies are clones of the real preview frame kept in
   step by a MutationObserver, so they inherit every custom property the shared
   controller writes. Hook attributes are scrubbed out of them, otherwise the
   shared controller would find a copy's nodes first.

   The dialog uses showModal(), which gives focus trapping and Esc dismissal
   from the browser rather than from hand-written key handling. Its controls
   ship hidden and are unhidden here, so if this file fails to run Step 2 falls
   back to what the Signature hero shows and Add to cart — outside the dialog —
   is untouched.

   No dependencies, no writes to any cp-sb-* file.
   ========================================================================== */

(() => {
  'use strict';

  const STATE_EVENT = 'cpsb:state';
  const MOBILE = '(max-width: 749px)';

  const readJson = (root, attr) => {
    const node = root.querySelector(`[${attr}]`);
    if (!node) return null;
    try {
      return JSON.parse(node.textContent);
    } catch (error) {
      console.warn('[cpnp] %s did not parse', attr, error);
      return null;
    }
  };

  /* ---- the copies -------------------------------------------------------- */

  const isHook = (name) =>
    name.indexOf('data-cpsb') === 0 ||
    name.indexOf('data-cpnp') === 0 ||
    name === 'id' ||
    name === 'for';

  function scrub(node) {
    if (node.nodeType !== 1) return;
    Array.prototype.slice.call(node.attributes).forEach((attr) => {
      if (isHook(attr.name)) node.removeAttribute(attr.name);
    });
    node.removeAttribute('fetchpriority');
    Array.prototype.forEach.call(node.children, scrub);
  }

  function copyAttributes(from, to, isRoot) {
    const wanted = {};
    Array.prototype.forEach.call(from.attributes, (attr) => {
      if (isHook(attr.name)) return;
      if (isRoot && attr.name === 'class') return; /* the copy carries its own */
      wanted[attr.name] = attr.value;
    });
    Array.prototype.slice.call(to.attributes).forEach((attr) => {
      if (attr.name in wanted) return;
      if (attr.name === 'aria-hidden') return;
      if (isRoot && (attr.name === 'class' || attr.name === 'style')) return;
      to.removeAttribute(attr.name);
    });
    Object.keys(wanted).forEach((name) => {
      if (to.getAttribute(name) !== wanted[name]) to.setAttribute(name, wanted[name]);
    });
  }

  /** Returns false when the shapes have diverged and a rebuild is needed. */
  function sync(from, to, isRoot) {
    if (from.tagName !== to.tagName) return false;
    if (from.children.length !== to.children.length) return false;
    copyAttributes(from, to, isRoot);
    if (!from.children.length) {
      if (to.textContent !== from.textContent) to.textContent = from.textContent;
      return true;
    }
    for (let i = 0; i < from.children.length; i++) {
      if (!sync(from.children[i], to.children[i], false)) return false;
    }
    return true;
  }

  function build(frame, into, className) {
    const copy = frame.cloneNode(true);
    scrub(copy);
    copy.classList.add(className);
    copy.setAttribute('aria-hidden', 'true');
    into.replaceChildren(copy);
    return copy;
  }

  /** How much is pinned above the section right now. */
  function pinnedHeight(section) {
    const selector = section.getAttribute('data-cpnp-sticky-selector');
    if (!selector) return 0;
    let total = 0;
    const seen = [];
    selector.split(',').forEach((part) => {
      const sel = part.trim();
      if (!sel) return;
      let nodes;
      try {
        nodes = document.querySelectorAll(sel);
      } catch (error) {
        return; /* a mistyped selector must never break the page */
      }
      Array.prototype.forEach.call(nodes, (node) => {
        if (seen.indexOf(node) !== -1) return;
        const position = window.getComputedStyle(node).position;
        if (position !== 'sticky' && position !== 'fixed') return;
        const rect = node.getBoundingClientRect();
        if (rect.height <= 0) return;
        seen.push(node);
        total += rect.height;
      });
    });
    return total;
  }

  /* The band's position depends on the preview padding and the baseline
     setting, so measure where the name actually sits and centre on it. Shrink
     the copy if the nameplate would not fit, so a tall frame or a stacked motif
     can never be clipped by the panel. */
  const FIT_MARGIN = 8;

  function fitBand(crop, copy) {
    const room = crop.clientHeight;
    if (!room) return;
    const row = copy.querySelector('.cpsb-preview__row--main');
    if (!row) return;

    // Clear last pass's transforms before measuring, or the numbers come back
    // already scaled and the fit walks a little further every render.
    copy.style.transform = '';
    row.style.transform = '';

    const offset = row.offsetTop;
    if (!offset) return;

    // 1. The window. It follows the lettering baseline, which is a percentage
    //    of the frame and therefore the same for every font and every design.
    //
    //    This used to scale and shift the whole copy to make the design fit,
    //    which moved the bowl itself: a tall design zoomed the panel out and a
    //    stacked one slid it up, so the preview looked like a different crop of
    //    a different bowl on every click. The window is now fixed and only what
    //    is printed on it moves.
    copy.style.transform = `translateY(${(room / 2 - offset).toFixed(1)}px)`;

    // 2. The design inside it. Offsets are read against the copy's own box, so
    //    they are unaffected by the translate just applied.
    const parts = copy.querySelectorAll(
      '.cpnp-plate, .cpnp-slot--frame, .cpnp-slot--above, ' +
        '.cpnp-slot--below, .cpnp-slot--side, .cpsb-preview__name'
    );
    const base = copy.getBoundingClientRect();
    let top = Infinity;
    let bottom = -Infinity;

    parts.forEach((el) => {
      const r = el.getBoundingClientRect();
      if (!r.width || !r.height) return;
      top = Math.min(top, r.top - base.top);
      bottom = Math.max(bottom, r.bottom - base.top);
    });

    if (top === Infinity || bottom <= top) return;

    const above = offset - top;
    const below = bottom - offset;
    const height = above + below;
    const usable = Math.max(0, room - FIT_MARGIN * 2);

    // Shrink only when the design genuinely does not fit, and only by as much
    // as it takes — most designs come out at 1 and are drawn untouched.
    const scale = height > usable && height ? usable / height : 1;

    // A design that stacks upwards sits well above the baseline, so centring it
    // in the window rather than hanging it off the baseline is what keeps the
    // shrink small: "Paws above" needs 0.94 this way against 0.55 without.
    const shift = -((below - above) * scale) / 2;

    row.style.transformOrigin = '50% 0';
    row.style.transform =
      `translateY(${shift.toFixed(1)}px) scale(${scale.toFixed(4)})`;
  }


  /* ---- the section ------------------------------------------------------- */

  class NameplateHero {
    constructor(root) {
      this.root = root;
      this.section = root.closest('.cpnp') || root;
      this.config = readJson(root, 'data-cpnp-config') || {};
      this.config.designs = this.config.designs || {};
      this.config.fonts = this.config.fonts || {};

      const q = (name) => root.querySelector(`[data-cpnp="${name}"]`);
      const qa = (name) => Array.from(root.querySelectorAll(`[data-cpnp="${name}"]`));

      this.el = {
        slots: {
          left: q('left'), right: q('right'),
          frame: q('frame'), above: q('above'), below: q('below'),
        },
        plate: root.querySelector('.cpnp-plate'),
        chips: Array.from(root.querySelectorAll('.cpnp-chip')),
        name: root.querySelector('[data-cpsb="name-input"]'),

        panel: q('panel'),
        crop: q('panel-crop'),

        full: q('full'),
        fullBody: q('full-body'),
        fullConfig: q('full-config'),
        openFull: qa('open-full'),
        closeFull: qa('close-full'),
        fullBtn: root.querySelector('.cpnp-fullbtn'),
      };

      this.copy = null;
      this.fullCopy = null;
      this.frameNodeRef = null;
      this.observer = null;
      this.queued = false;
      this.opener = null;
      this.mq = window.matchMedia(MOBILE);

      this.bind();
      this.measure();
      this.paintPlate();
      this.fitInFrame();
      this.paintChips();
      this.paint();
      this.reveal();
    }

    on(target, type, handler, options) {
      if (!target) return;
      target.addEventListener(type, handler, options);
    }

    bind() {
      // The shared controller re-renders on every change and broadcasts last,
      // so this runs after it has repainted the name and the colour.
      this.on(document, STATE_EVENT, (event) => {
        if (!event.detail || event.detail.sectionId !== this.root.dataset.cpsbSection) return;
        this.paintPlate();
      this.fitInFrame();
        this.paintChips();
      });

      // Typing does not change the shared state on every keystroke, and the
      // chips have to keep up with it.
      this.on(this.el.name, 'input', () => this.paintChips());

      this.on(this.root, 'change', (event) => {
        if (event.target.closest('[data-cpsb="design-input"], [data-cpsb="font-input"]')) {
          this.paintPlate();
      this.fitInFrame();
          this.paintChips();
        }
      });

      this.bindFull();

      let resizing = null;
      this.on(window, 'resize', () => {
        window.clearTimeout(resizing);
        resizing = window.setTimeout(() => {
          this.measure();
          this.schedule();
        }, 120);
      });

      const onMedia = () => {
        // Crossing 750px changes whether the pinned preview belongs on screen.
        this.reveal();
        this.measure();
        this.schedule();
      };
      if (typeof this.mq.addEventListener === 'function') {
        this.mq.addEventListener('change', onMedia);
      } else if (typeof this.mq.addListener === 'function') {
        this.mq.addListener(onMedia); /* older Safari */
      }

      if (document.fonts && document.fonts.ready) {
        document.fonts.ready.then(() => {
          this.paintChips();
          this.schedule();
        }).catch(() => {});
      }
    }

    /* The pinned preview, its Enlarge control and the full-preview button are
       a narrow-viewport affordance: above 750px the real preview is already on
       screen beside Step 2, so a second copy of it is noise. They ship hidden
       and are only unhidden below that width, which also means none of them
       appears if this script never runs. */
    reveal() {
      const narrow = this.mq.matches;
      if (this.el.panel) this.el.panel.hidden = !narrow;
      if (this.el.fullBtn) this.el.fullBtn.hidden = !narrow;
    }

    /* ------------------------------------------------------------- state -- */

    checked(hook) {
      return this.root.querySelector(`[data-cpsb="${hook}"]:checked`);
    }

    currentLayout() {
      const el = this.checked('design-input');
      return this.config.designs[el ? el.dataset.cpsbBlockId : ''] || {};
    }

    currentFont() {
      const el = this.checked('font-input');
      return this.config.fonts[el ? el.dataset.cpsbBlockId : ''] || {};
    }

    /** The name as it prints: trimmed, and upper case if the face asks for it. */
    printedName() {
      const font = this.currentFont();
      const typed = (this.el.name ? this.el.name.value : '').trim();
      const shown = typed || this.config.chipPlaceholder || 'Crumpaw';
      return font.caps ? shown.toUpperCase() : shown;
    }

    /* --------------------------------------------------------- nameplate -- */

    motif(key) {
      if (!key) return null;
      const template = this.root.querySelector(`[data-cpnp-motif="${key}"]`);
      if (!template || !('content' in template)) return null;
      return template.content.cloneNode(true);
    }

    /* Fit the name inside a frame motif.

       A frame is a hole the lettering has to live in, and the hole is a fixed
       share of the motif: the bone's is the 152 x 30 slot of its 220 x 76 box.
       The name is sized from its own length and the face's optical size, which
       knows nothing about that hole — so the ten faces landed all over it. The
       widths were always fine; it was the heights that broke, and the scripts
       worst of all, because an ascender and a descender together are close to
       twice the bar's depth. "Cooper" in Fine Script measured 92px of ink in a
       47.6px bar.

       Ink is measured rather than the layout box: a script's swashes and tails
       overflow their advance width, so the box says it fits while the glyphs
       are visibly outside. Canvas is used because it is the only thing that
       reports real ink extents for arbitrary text.

       Nothing is applied unless a frame is present and the ink actually
       overflows, so the nine other designs are untouched.
    */
    fitInFrame() {
      const { el } = this;
      const name = el.plate && el.plate.querySelector('.cpsb-preview__name');
      if (!name) return;

      // Always start from the unscaled state, or successive passes compound.
      name.style.scale = '';

      const frame = el.slots && el.slots.frame;
      const art = frame && frame.firstElementChild;
      if (!art) return;

      const box = art.getBoundingClientRect();
      if (!box.width || !box.height) return;

      const hole = this.frameHole(this.currentLayout().frame);
      if (!hole) return;

      const room = {
        w: (box.width * hole.w) / hole.vbW,
        h: (box.height * hole.h) / hole.vbH,
      };
      if (room.w <= 0 || room.h <= 0) return;

      const cs = window.getComputedStyle(name);
      const size = parseFloat(cs.fontSize);
      if (!size) return;

      if (!this.gauge) this.gauge = document.createElement('canvas').getContext('2d');
      if (!this.gauge) return;
      this.gauge.font =
        `${cs.fontStyle} ${cs.fontWeight} ${cs.fontSize} ${cs.fontFamily}`;
      const m = this.gauge.measureText(name.textContent || '');
      const inkW = (m.actualBoundingBoxLeft || 0) + (m.actualBoundingBoxRight || 0);
      const inkH = (m.actualBoundingBoxAscent || 0) + (m.actualBoundingBoxDescent || 0);
      if (!inkW || !inkH) return;

      const scale = Math.min(1, room.w / inkW, room.h / inkH);
      if (scale >= 0.999) return;

      // Scaled rather than resized. The plate takes its width from the name,
      // and the frame is a percentage of the plate — so shrinking the font
      // shrinks the bone with it and the lettering never catches up. The CSS
      // `scale` property leaves layout alone, so the bone keeps the size it
      // would have had and only the glyphs come down inside it. It is also a
      // separate property from `transform`, so the shared barrel curve on the
      // preview name survives.
      name.style.scale = scale.toFixed(4);
    }

    /* The usable hole inside each frame motif, in its own viewBox units. The
       width is trimmed by the corner radius at each end so a glyph cannot sit
       in the rounded part. */
    frameHole(key) {
      const HOLES = {
        bone: { vbW: 220, vbH: 76, w: 152 - 30, h: 30 },
      };
      return HOLES[key] || null;
    }

    paintPlate() {
      const { el } = this;
      if (!el.plate) return;
      const layout = this.currentLayout();

      const put = (node, key) => {
        if (!node) return;
        node.textContent = '';
        const art = this.motif(key);
        if (art) node.appendChild(art);
      };

      put(el.slots.left, layout.left);
      put(el.slots.right, layout.right);
      put(el.slots.frame, layout.frame);
      put(el.slots.above, layout.above);
      put(el.slots.below, layout.below);

      const style = el.plate.style;
      const num = (v, fallback) => String(v == null ? fallback : v);
      style.setProperty('--cpnp-name-scale', num(layout.nameScale, 1));
      style.setProperty('--cpnp-frame-w', `${num(layout.frameW, 192)}%`);
      style.setProperty('--cpnp-frame-dy', `${num(layout.frameDy, 0)}em`);
      style.setProperty('--cpnp-above-w', `${num(layout.aboveW, 78)}%`);
      style.setProperty('--cpnp-above-dy', `${num(layout.aboveDy, 0)}em`);
      style.setProperty('--cpnp-below-w', `${num(layout.belowW, 100)}%`);
      style.setProperty('--cpnp-below-dy', `${num(layout.belowDy, 0)}em`);

      // The side slots sit outside the plate, so they carry their own.
      const side = (node, k, dx, dy) => {
        if (!node) return;
        node.style.setProperty('--cpnp-side-k', num(k, 0.6) / 100);
        node.style.setProperty('--cpnp-side-dx', `${num(dx, 0)}em`);
        node.style.setProperty('--cpnp-side-dy', `${num(dy, 0)}em`);
        node.style.setProperty('--cpnp-name-scale', num(layout.nameScale, 1));
      };
      side(el.slots.left, layout.leftW, layout.leftDx, layout.leftDy);
      side(el.slots.right, layout.rightW, layout.rightDx, layout.rightDy);

      // The bone's hole takes the bowl's band colour so the name reads through.
      const colours = this.config.colours || [];
      const colourEl = this.checked('colour-input');
      const index = colourEl
        ? Array.from(this.root.querySelectorAll('[data-cpsb="colour-input"]')).indexOf(colourEl)
        : 0;
      const band = colours[index < 0 ? 0 : index];
      if (band) this.section.style.setProperty('--cpnp-band', band);

      this.schedule();
    }

    /* ------------------------------------------------------------- chips -- */

    /**
     * Each chip is the finished band in miniature. The motifs are already drawn
     * by Liquid — they never change for a layout — so only the name, its face
     * and its size are touched here, which keeps typing cheap.
     */
    paintChips() {
      const font = this.currentFont();
      const max = this.config.chipMaxChars || 8;
      const text = this.printedName().slice(0, max);
      // The design fits the chip name to its length rather than to its box.
      const fit = Math.min(1, (60 / Math.max(text.length, 4)) * 1.6 / 13) * (font.k || 1);

      this.el.chips.forEach((chip) => {
        const label = chip.querySelector('[data-cpnp-chip="name"]');
        if (!label) return;
        if (label.textContent !== text) label.textContent = text;
        const style = chip.style;
        style.setProperty('--cpnp-chip-fit', fit.toFixed(3));
        if (font.family) style.setProperty('font-family', font.family);
        if (font.weight) style.setProperty('font-weight', String(font.weight));
      });
    }

    /* ----------------------------------------------------------- preview -- */

    frameNode() {
      const frame = this.root.querySelector('[data-cpsb="preview-frame"]');
      if (frame !== this.frameNodeRef) {
        this.frameNodeRef = frame;
        this.copy = null;
        this.fullCopy = null;
        this.watch();
      }
      return frame;
    }

    watch() {
      if (this.observer) this.observer.disconnect();
      if (!this.frameNodeRef || !window.MutationObserver) return;
      this.observer = new MutationObserver(() => this.schedule());
      this.observer.observe(this.frameNodeRef, {
        subtree: true,
        childList: true,
        attributes: true,
        characterData: true,
      });
    }

    schedule() {
      if (this.queued) return;
      this.queued = true;
      window.requestAnimationFrame(() => {
        this.queued = false;
        this.paint();
      });
    }

    paint() {
      const frame = this.frameNode();
      if (!frame) return;

      // Before the copies are taken, so they inherit the fitted size rather
      // than trailing it by a frame. The name itself is written by the shared
      // controller, so this is the point where its new text is on the page.
      this.fitInFrame();

      if (this.el.crop) {
        if (!this.copy || !this.copy.isConnected || !sync(frame, this.copy, true)) {
          this.copy = build(frame, this.el.crop, 'cpnp-panel__inner');
        }
        fitBand(this.el.crop, this.copy);
        if (this.el.panel) this.el.panel.dataset.cpnpReady = 'on';
      }

      // The dialog copy is only worth keeping current while it is on screen.
      if (this.el.fullBody && this.isFullOpen()) {
        if (!this.fullCopy || !this.fullCopy.isConnected || !sync(frame, this.fullCopy, true)) {
          this.fullCopy = build(frame, this.el.fullBody, 'cpnp-full__inner');
        }
        if (this.el.fullConfig) {
          const config = frame.querySelector('.cpsb-preview__config');
          this.el.fullConfig.textContent = config ? config.textContent.trim() : '';
        }
      }
    }

    measure() {
      this.section.style.setProperty(
        '--cpnp-panel-top',
        `${pinnedHeight(this.section).toFixed(1)}px`
      );
    }

    /* --------------------------------------------------------- full view -- */

    bindFull() {
      const { el } = this;
      el.openFull.forEach((btn) => this.on(btn, 'click', () => this.openFull(btn)));
      el.closeFull.forEach((btn) => this.on(btn, 'click', () => this.closeFull()));
      // Clicking the backdrop lands on the dialog itself, never on the card.
      this.on(el.full, 'click', (event) => {
        if (event.target === el.full) this.closeFull();
      });
      this.on(el.full, 'close', () => {
        if (this.opener && this.opener.isConnected) this.opener.focus();
        this.opener = null;
      });
    }

    isFullOpen() {
      return Boolean(this.el.full && this.el.full.open);
    }

    openFull(trigger) {
      const dialog = this.el.full;
      if (!dialog || this.isFullOpen()) return;
      this.opener = trigger || null;
      // showModal gives the focus trap and Esc handling; the fallback is for
      // engines old enough to lack it, where the dialog is at least readable.
      if (typeof dialog.showModal === 'function') {
        dialog.showModal();
      } else {
        dialog.setAttribute('open', '');
      }
      this.paint();
    }

    closeFull() {
      const dialog = this.el.full;
      if (!dialog || !this.isFullOpen()) return;
      if (typeof dialog.close === 'function') {
        dialog.close();
      } else {
        dialog.removeAttribute('open');
      }
    }
  }

  const start = () => {
    document.querySelectorAll('.cpnp cpsb-personalizer').forEach((root) => {
      if (root.dataset.cpnpReady === 'true') return;
      root.dataset.cpnpReady = 'true';
      try {
        new NameplateHero(root);
      } catch (error) {
        // A broken nameplate must not take the rest of the page with it.
        console.warn('[cpnp] init failed', error);
      }
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }

  // The theme editor re-renders sections in place.
  document.addEventListener('shopify:section:load', start);
})();


/* ---------------------------------------------------------------------------
   Colour row — bring the chosen card into view

   The row starts at scroll position zero, which is right when the chosen
   colourway is the first one and wrong the moment a variant URL selects a
   later one. This nudges the row so the chosen card is the one on screen.

   scrollLeft is set directly rather than calling scrollIntoView, which would
   scroll the page vertically as well and move Step 2 out from under the
   customer. Rects are used instead of offsetLeft so no assumption is made
   about which ancestor is positioned.

   It runs once and stands down the moment the customer touches the row, so it
   can never fight a scroll in progress.
   ------------------------------------------------------------------------ */
(function () {
  var MOBILE = '(max-width: 749px)';

  function align(row) {
    var checked = row.parentNode.querySelector('input:checked');
    if (!checked || !checked.id) return;
    var label;
    try {
      label = row.querySelector(
        'label[for="' + (window.CSS && CSS.escape ? CSS.escape(checked.id) : checked.id) + '"]'
      );
    } catch (error) {
      return;
    }
    if (!label) return;
    var pad = parseFloat(getComputedStyle(row).paddingLeft) || 0;
    var delta = label.getBoundingClientRect().left - row.getBoundingClientRect().left - pad;
    if (Math.abs(delta) < 1) return;
    row.scrollLeft = Math.max(0, row.scrollLeft + delta);
  }

  function arm() {
    if (!window.matchMedia(MOBILE).matches) return;
    var rows = document.querySelectorAll('[data-cpsb-colour-row] .cpsb-chips');
    Array.prototype.forEach.call(rows, function (row) {
      if (row.dataset.cpsbColourArmed === 'on') return;
      row.dataset.cpsbColourArmed = 'on';
      var touched = false;
      ['pointerdown', 'wheel', 'touchstart'].forEach(function (event) {
        row.addEventListener(event, function () { touched = true; }, { passive: true, once: true });
      });
      window.requestAnimationFrame(function () {
        if (!touched && row.scrollWidth > row.clientWidth) align(row);
      });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', arm);
  } else {
    arm();
  }
  document.addEventListener('shopify:section:load', arm);
})();
